'use client';

import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Search, CircularProgress } from 'src/components/PulseUI';
import { useDebounce } from 'use-debounce';
import { useGetTargetStrategyConnectorsInfiniteQuery, useGetObjectDefinitionsListQuery, useGetFieldDefinitionsQuery, useGetMappingsQuery } from 'src/api/redux';
import {
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
  SortSection,
} from 'src/components';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import { StatusCell } from '../StatusCell';

const TargetConnectors = ({ strategyId }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    sort: string;
    order: 'asc' | 'desc';
  }>({
    sort: 'list_items_count',
    order: 'desc',
  });

  // Menu state for column dropdowns
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Infinite scroll refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 50;

  // Determine if the company uses MedRM by checking enabled mappings
  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const isMedRM = useMemo(
    () =>
      allMappings?.results?.some(
        (mapping) => mapping.enabled && mapping.crm?.toLowerCase() === 'medrm'
      ) ?? false,
    [allMappings]
  );

  // For MedRM, fetch field definitions to map status values to labels
  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery(
    undefined,
    { skip: !isMedRM }
  );
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
    { objectId: objectDefinitions?.results?.[0]?.id },
    { skip: !isMedRM || !objectDefinitions?.results?.[0]?.id }
  );

  // Create a lookup map from status value to label from MedRM field definitions
  const statusLabelMap = useMemo(() => {
    if (!isMedRM) return new Map<string, string>();
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(
      statusField.options.map((opt) => [opt.value, opt.label])
    );
  }, [isMedRM, fieldDefinitions]);

  // Columns - must be defined before usePulseTableColumns
  const columns: PulseColumnDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        resizable: true,
        sortable: true,
        renderCell: (value: string) => (
          <StatusCell
            status={value}
            displayLabel={statusLabelMap.get(value)}
          />
        ),
      },
      {
        field: 'territory__name',
        headerName: 'Territory Name',
        width: 300,
        minWidth: 200,
        maxWidth: 500,
        resizable: true,
        sortable: true,
        renderCell: (value: string) => (
          <span className="truncate" title={value}>
            {value}
          </span>
        ),
      },
      {
        field: 'list_items_count',
        headerName: 'List Items',
        width: 150,
        minWidth: 100,
        maxWidth: 300,
        resizable: true,
        sortable: true,
        renderCell: (value: number) => (
          <span className="text-neutral-700">{handleNumericValue(value)}</span>
        ),
      },
    ],
    [statusLabelMap]
  );

  // Column resizing management - must come after columns definition
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  // Handle menu toggle
  const handleEllipsisClick = (e: React.MouseEvent, columnId: string) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === columnId ? null : columnId));
  };

  // Handle sort changes
  const handleSortToggle = (columnName: string, order?: 'asc' | 'desc') => {
    setSortConfig((prevSort) => {
      const newOrder =
        order ||
        (prevSort.sort === columnName && prevSort.order === 'asc'
          ? 'desc'
          : 'asc');
      return {
        sort: columnName,
        order: newOrder,
      };
    });
    setOpenMenuId(null);
  };

  // Fetch with infinite query
  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetTargetStrategyConnectorsInfiniteQuery(
    {
      strategyId,
      params: {
        q: debouncedSearch,
        ...sortConfig,
        page_size: PAGE_SIZE,
      },
    },
    {
      skip: !strategyId,
    }
  );

  // Flatten all pages into a single results array
  const results = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results);
  }, [data?.pages]);

  // Scroll detection for infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when near bottom
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  return (
    <div className="flex flex-col mx-auto w-full">
      <div
        className="flex flex-col gap-6 bg-white rounded-xl border 
      border-neutral-200 shadow-sm p-6"
      >
        <Search
          value={search}
          onSearch={setSearch}
          placeholder="Search Target Statuses..."
          className="rounded-full max-w-96"
        />
        {/* Table */}
        <div
          className="flex-1 flex flex-col overflow-hidden bg-white 
        relative rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.00)] 
        border border-neutral-200"
        >
          {/* Table container with scrolling */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="overflow-x-auto overflow-y-auto relative min-h-96 max-h-144"
          >
            {/* Grid table */}
            <div
              role="table"
              className="grid relative w-full"
              style={{
                gridTemplateColumns: getGridTemplateColumns(),
                gridAutoFlow: 'row',
              }}
            >
              {/* Header Row */}
              {columns.map((column) => {
                const isMenuOpen = openMenuId === column.field;
                const isActiveSortColumn = sortConfig.sort === column.field;

                return (
                  <PulseHeader
                    key={column.field}
                    label={column.headerName}
                    isSticky
                    stickyPosition="top"
                    className="sticky top-0 z-20 bg-neutral-50 text-neutral-900 border-b border-r border-b-neutral-200 border-r-neutral-200"
                    sortable={column.sortable}
                    sortDirection={isActiveSortColumn ? sortConfig.order : null}
                    resizable={column.resizable}
                    onStartResize={
                      column.resizable
                        ? (e) => startResizing(column.field, e)
                        : undefined
                    }
                    isResizing={isResizing}
                    menu={
                      column.sortable ? (
                        <PulseColumnMenu
                          columnId={column.field}
                          isOpen={isMenuOpen}
                          onToggle={(e) => handleEllipsisClick(e, column.field)}
                          customSectionComponents={[
                            <SortSection
                              key="sort"
                              currentOrder={sortConfig.order}
                              isActive={isActiveSortColumn}
                              onChange={(order) =>
                                handleSortToggle(column.field, order)
                              }
                            />,
                          ]}
                        />
                      ) : undefined
                    }
                  />
                );
              })}

              {/* Filler Header */}
              <PulseHeader
                label=""
                isSticky
                stickyPosition="top"
                className="sticky top-0 z-20 bg-neutral-50 border-b border-b-neutral-200"
                resizable={false}
              />

              {/* Data Rows */}
              {results.map((row) => (
                <React.Fragment key={row.id}>
                  {columns.map((column) => {
                    const value = row[column.field];
                    const cellContent = column.renderCell
                      ? column.renderCell(value, row)
                      : value;

                    return (
                      <PulseCell
                        key={`${row.id}-${column.field}`}
                        className="bg-white border-b border-r
                          border-b-neutral-200 border-r-neutral-200 h-12"
                      >
                        {cellContent}
                      </PulseCell>
                    );
                  })}
                  {/* Filler Cell - matches the filler column */}
                  <PulseCell
                    key={`${row.id}-filler`}
                    className="bg-white border-b border-b-neutral-200 h-12"
                  >
                    <span />
                  </PulseCell>
                </React.Fragment>
              ))}
            </div>

            {/* Loading more indicator at bottom */}
            {(isFetchingNextPage || (isFetching && !isLoading && hasNextPage)) && (
              <div className="flex items-center justify-center py-4">
                <CircularProgress size="sm" color="neutral" />
              </div>
            )}
          </div>

          {/* Loading overlay - only for initial load */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-40">
              <PulseLoading />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !isFetching && results.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
                <p className="text-lg font-medium">No connectors found</p>
                {debouncedSearch && (
                  <p className="text-sm">Try adjusting your search criteria</p>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default TargetConnectors;
